/**
 * DuplicateEAModal Component (P9-B)
 * Shows when a duplicate EA is detected via hash check
 *
 * Offers user options:
 * 1. Use existing analysis (instant, 0 tokens)
 * 2. Re-analyze (new analysis, uses tokens)
 */

import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    IconButton,
    Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { getFileTypeConfig } from '../../../api/qbridgeEaApi';

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date
 */
const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return dateString;
    }
};

/**
 * Get quality score color
 * @param {number} score - Quality score (0-10)
 * @returns {string} - Hex color
 */
const getScoreColor = (score) => {
    if (score >= 8) return '#27B56D'; // Green
    if (score >= 6) return '#F59E0B'; // Yellow/Orange
    if (score >= 4) return '#EF4444'; // Red
    return '#6B7280'; // Gray
};

/**
 * DuplicateEAModal Component
 * @param {boolean} open - Dialog visibility
 * @param {function} onClose - Close handler
 * @param {function} onUseExisting - Handler for using existing analysis
 * @param {function} onReanalyze - Handler for re-analyzing
 * @param {object} duplicateInfo - Duplicate detection result from API
 * @param {object} file - Current EA file being analyzed
 * @param {boolean} isLoading - Loading state
 */
const DuplicateEAModal = ({
    open = false,
    onClose,
    onUseExisting,
    onReanalyze,
    duplicateInfo = null,
    file = null,
    isLoading = false
}) => {
    if (!duplicateInfo || !duplicateInfo.exists) return null;

    const fileConfig = file ? getFileTypeConfig(file.file_type) : { color: '#7c3aed', label: 'EA' };
    const scoreColor = getScoreColor(duplicateInfo.qualityScore);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: '#0F0F14',
                    backgroundImage: 'none',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '16px',
                    maxWidth: '480px'
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px 24px 16px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <HistoryIcon sx={{ color: '#FFFFFF', fontSize: '1.5rem' }} />
                    </Box>
                    <Box>
                        <Typography
                            sx={{
                                color: '#FFFFFF',
                                fontSize: '1.125rem',
                                fontWeight: 600
                            }}
                        >
                            Previous Analysis Found
                        </Typography>
                        <Typography
                            sx={{
                                color: 'rgba(255, 255, 255, 0.5)',
                                fontSize: '0.8125rem'
                            }}
                        >
                            This EA has been analyzed before
                        </Typography>
                    </Box>
                </Box>

                <IconButton
                    onClick={onClose}
                    disabled={isLoading}
                    sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        '&:hover': {
                            color: '#FFFFFF',
                            backgroundColor: 'rgba(255, 255, 255, 0.08)'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* Content */}
            <DialogContent sx={{ padding: '24px' }}>
                {/* Existing Analysis Card */}
                <Box
                    sx={{
                        padding: '20px',
                        backgroundColor: 'rgba(245, 158, 11, 0.08)',
                        borderRadius: '12px',
                        border: '1px solid rgba(245, 158, 11, 0.2)',
                        mb: 3
                    }}
                >
                    {/* File Name */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <CheckCircleIcon sx={{ color: '#27B56D', fontSize: '1.25rem' }} />
                        <Typography
                            sx={{
                                color: '#FFFFFF',
                                fontSize: '1rem',
                                fontWeight: 600,
                                fontFamily: "'JetBrains Mono', monospace",
                                flex: 1,
                                wordBreak: 'break-all'
                            }}
                        >
                            {duplicateInfo.eaName || file?.name || 'Unknown EA'}
                        </Typography>
                        <Chip
                            label={fileConfig.label}
                            size="small"
                            sx={{
                                backgroundColor: `${fileConfig.color}20`,
                                color: fileConfig.color,
                                fontWeight: 700,
                                fontSize: '0.6875rem',
                                border: `1px solid ${fileConfig.color}40`
                            }}
                        />
                    </Box>

                    {/* Stats Row */}
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {/* Quality Score */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <StarIcon sx={{ color: scoreColor, fontSize: '1rem' }} />
                            <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8125rem' }}>
                                Quality:
                            </Typography>
                            <Typography
                                sx={{
                                    color: scoreColor,
                                    fontSize: '0.875rem',
                                    fontWeight: 600
                                }}
                            >
                                {duplicateInfo.qualityScore?.toFixed(1) || 'N/A'}/10
                            </Typography>
                        </Box>

                        {/* Analyzed Date */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '1rem' }} />
                            <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8125rem' }}>
                                {formatDate(duplicateInfo.createdAt)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Message */}
                <Typography
                    sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '0.9375rem',
                        lineHeight: 1.6,
                        mb: 2
                    }}
                >
                    {duplicateInfo.message || 'This EA has been analyzed previously. Would you like to use the existing analysis or run a new one?'}
                </Typography>

                {/* Options Explanation */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '6px',
                                backgroundColor: 'rgba(39, 181, 109, 0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                mt: 0.25
                            }}
                        >
                            <Typography sx={{ color: '#27B56D', fontSize: '0.75rem', fontWeight: 700 }}>1</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#FFFFFF', fontSize: '0.875rem', fontWeight: 600 }}>
                                Use Existing Analysis
                            </Typography>
                            <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8125rem' }}>
                                Instant results, no tokens used
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '6px',
                                backgroundColor: 'rgba(124, 58, 237, 0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                mt: 0.25
                            }}
                        >
                            <Typography sx={{ color: '#a855f7', fontSize: '0.75rem', fontWeight: 700 }}>2</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#FFFFFF', fontSize: '0.875rem', fontWeight: 600 }}>
                                Re-analyze
                            </Typography>
                            <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8125rem' }}>
                                Fresh analysis with latest AI model
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            {/* Footer */}
            <DialogActions
                sx={{
                    padding: '16px 24px 24px',
                    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                    gap: 1.5,
                    flexDirection: { xs: 'column', sm: 'row' }
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={isLoading}
                    sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        textTransform: 'none',
                        fontWeight: 500,
                        padding: '10px 20px',
                        order: { xs: 3, sm: 1 },
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.08)'
                        }
                    }}
                >
                    Cancel
                </Button>

                <Button
                    onClick={onReanalyze}
                    disabled={isLoading}
                    startIcon={<RefreshIcon />}
                    sx={{
                        backgroundColor: 'rgba(124, 58, 237, 0.15)',
                        border: '1px solid rgba(124, 58, 237, 0.3)',
                        color: '#a855f7',
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: '10px 20px',
                        borderRadius: '8px',
                        order: { xs: 2, sm: 2 },
                        width: { xs: '100%', sm: 'auto' },
                        '&:hover': {
                            backgroundColor: 'rgba(124, 58, 237, 0.25)',
                            borderColor: 'rgba(124, 58, 237, 0.5)'
                        },
                        '&.Mui-disabled': {
                            backgroundColor: 'rgba(124, 58, 237, 0.1)',
                            color: 'rgba(168, 85, 247, 0.5)'
                        }
                    }}
                >
                    Re-analyze
                </Button>

                <Button
                    onClick={onUseExisting}
                    disabled={isLoading}
                    startIcon={<CheckCircleIcon />}
                    sx={{
                        background: 'linear-gradient(135deg, #27B56D 0%, #10B981 100%)',
                        color: '#FFFFFF',
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: '10px 24px',
                        borderRadius: '8px',
                        minWidth: '160px',
                        order: { xs: 1, sm: 3 },
                        width: { xs: '100%', sm: 'auto' },
                        '&:hover': {
                            background: 'linear-gradient(135deg, #1E9D5C 0%, #059669 100%)'
                        },
                        '&.Mui-disabled': {
                            background: 'rgba(39, 181, 109, 0.3)',
                            color: 'rgba(255, 255, 255, 0.5)'
                        }
                    }}
                >
                    {isLoading ? 'Loading...' : 'Use Existing'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DuplicateEAModal;
